/**
 * Copyright 2026 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
// @ts-check
import { Task } from '@lit/task';
import '@shoelace-style/shoelace';
import { LitElement, css, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { SHARED_STYLES } from '../css/shared-css.js';
let ChromedashReportFeatureLatencyPage = class ChromedashReportFeatureLatencyPage extends LitElement {
    constructor() {
        super(...arguments);
        this.rawQuery = {};
        this._client = window.csOpenApiClient;
        this.latencyList = [];
        this._latencyTask = new Task(this, {
            task: async ([startAt, endAt], { signal }) => {
                this.latencyList = await this._client.listFeatureLatency({ startAt, endAt }, { signal });
                return this.latencyList;
            },
            args: () => [this.startAtDate, this.endAtDate],
        });
        this.startAtDate = new Date('January 1, 2023');
        this.endAtDate = new Date('January 1, 2024');
    }
    static get styles() {
        return [
            ...SHARED_STYLES,
            css `
        p {
          margin: var(--content-padding) 0;
        }

        table {
          border: var(--table-divider);
        }
        td,
        th {
          padding: var(--content-padding);
          border-bottom: var(--table-divider);
          vertical-align: baseline;
        }
        th {
          background: var(--table-header-background);
        }
        tr {
          background: var(--table-row-background);
        }
      `,
        ];
    }
    static get properties() {
        return {
            latencyList: { type: Array },
            startAtDate: { state: true },
            endAtDate: { state: true },
        };
    }
    connectedCallback() {
        super.connectedCallback();
        this.initializeParams();
    }
    initializeParams() {
        if (!this.rawQuery) {
            return;
        }
        if (this.rawQuery['startAt']) {
            const parsed = Date.parse(this.rawQuery['startAt']);
            if (!isNaN(parsed)) {
                this.startAtDate = new Date(parsed);
            }
        }
        if (this.rawQuery['endAt']) {
            const parsed = Date.parse(this.rawQuery['endAt']);
            if (!isNaN(parsed)) {
                this.endAtDate = new Date(parsed);
            }
        }
    }
    renderCount() {
        return html `
      <p>
        Launched ${this.latencyList.length} features between
        ${this.startAtDate.toLocaleDateString()} and
        ${this.endAtDate.toLocaleDateString()}.
      </p>
      <p>Latency is measured in calendar days.</p>
    `;
    }
    renderLatencyRow(featureLatency) {
        const latencyMs = featureLatency.shipped_date - featureLatency.entry_created_date;
        const latencyDays = Math.ceil(latencyMs / (1000 * 3600 * 24));
        return html `
      <tr>
        <td>
          <a href="/feature/${featureLatency.feature.id}">
            ${featureLatency.feature.name}
          </a>
        </td>
        <td>
          ${featureLatency.owner_emails.map(addr => html `<div>${addr}</div>`)}
        </td>
        <td>${featureLatency.entry_created_date.toLocaleDateString()}</td>
        <td>${featureLatency.shipped_date.toLocaleDateString()}</td>
        <td>${latencyDays}</td>
      </tr>
    `;
    }
    renderTable() {
        return html `
      <table>
        <tr>
          <th>Feature</th>
          <th>Owners</th>
          <th>Created</th>
          <th>Shipped</th>
          <th>Latency</th>
        </tr>
        ${this.latencyList.map(fl => this.renderLatencyRow(fl))}
      </table>
    `;
    }
    renderWhenComplete() {
        return html ` ${this.renderCount()} ${this.renderTable()} `;
    }
    render() {
        return this._latencyTask.render({
            initial: () => html `<p>Loading...</p>`,
            pending: () => html `<p>Loading...</p>`,
            complete: () => {
                return this.renderWhenComplete();
            },
            error: e => {
                console.error(e);
                return html `<p>
          Some errors occurred. Please refresh the page or try again later.
        </p>`;
            },
        });
    }
};
__decorate([
    property({ type: Object })
], ChromedashReportFeatureLatencyPage.prototype, "rawQuery", void 0);
__decorate([
    property({ attribute: false })
], ChromedashReportFeatureLatencyPage.prototype, "_client", void 0);
__decorate([
    property({ attribute: false })
], ChromedashReportFeatureLatencyPage.prototype, "latencyList", void 0);
__decorate([
    property({ attribute: false })
], ChromedashReportFeatureLatencyPage.prototype, "_latencyTask", void 0);
__decorate([
    state() // Default to entire year 2023.
], ChromedashReportFeatureLatencyPage.prototype, "startAtDate", void 0);
__decorate([
    state()
], ChromedashReportFeatureLatencyPage.prototype, "endAtDate", void 0);
ChromedashReportFeatureLatencyPage = __decorate([
    customElement('chromedash-report-feature-latency-page')
], ChromedashReportFeatureLatencyPage);
export { ChromedashReportFeatureLatencyPage };
